import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, Plus, Minus, Save } from 'lucide-react';
import { createTeamsMeeting } from '../../services/teamsService';
import { authAPI } from '../../services/apiService';
import { useMsal } from "@azure/msal-react";
import { protectedResources } from '../../utils/msalConfig';
import RoomSelector from '../common/RoomSelector';
import { v4 as uuidv4 } from 'uuid';

const CreateMeeting = ({ onMeetingCreated, onMeetingCancelled }) => {
    const getNextAvailableTime = () => {
        const now = new Date();
        let hour = now.getHours();
        let minute = now.getMinutes();
    
        // Determine the next closest available time (0 or 30)
        if (minute < 30) {
            minute = 30;
        } else {
            minute = 0;
            hour += 1; // Move to the next hour if past 30
        }
    
        // Format time properly (HH:MM)
        const formattedHour = hour.toString().padStart(2, "0");
        const formattedMinute = minute.toString().padStart(2, "0");
    
        return `${formattedHour}:${formattedMinute}`;
    };
    const { instance } = useMsal();
    const [baseFormData, setBaseFormData] = useState({
        titleBase: '',
        description: '',
        maxParticipants: '',
        startTimeBase: getNextAvailableTime(),
    });
    
    const [meetings, setMeetings] = useState([{
        id: uuidv4(),
        date: '',
        startTime: '',
        endTime: '',
        selectedRoom: null
    }]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [delegatedToken, setDelegatedToken] = useState(null);

    useEffect(() => {
        const getDelegatedToken = async () => {
            const accounts = instance.getAllAccounts();
            if (accounts.length === 0) return;
            console.log('Attempting to acquire token with scopes:', protectedResources.meetings.scopes);
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: protectedResources.meetings.scopes,
                account: accounts[0],
                forceRefresh: true
            });
            
            setDelegatedToken(tokenResponse.accessToken);
        };

        getDelegatedToken();
    }, [instance]);

    const handleAddMeeting = () => {
        setMeetings(prev => [...prev, {
            id: uuidv4(),
            date: '',
            startTime: '',
            endTime: '',
            selectedRoom: null
        }]);
    };

    const handleRemoveMeeting = (id) => {
        if (meetings.length > 1) {
            setMeetings(prev => prev.filter(meeting => meeting.id !== id));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const tokenData = await authAPI.getToken();
            const accounts = instance.getAllAccounts();
            
            if (accounts.length === 0) {
                throw new Error('No accounts found. Please sign in.');
            }

            const createdMeetings = await Promise.all(meetings.map(async (meeting) => {
                const meetingData = {
                    title: baseFormData.titleBase,
                    date: meeting.date,
                    startTime: meeting.startTime,
                    endTime: meeting.endTime,
                    description: baseFormData.description,
                    maxParticipants: baseFormData.maxParticipants,
                    selectedRoom: meeting.selectedRoom
                };

                const meetingResponse = await createTeamsMeeting(meetingData, delegatedToken);

                return {
                    ...meetingResponse,
                    rsvpList: [{"email": meeting.selectedRoom.emailAddress, "name": meeting.selectedRoom.displayName}],
                    createdAt: new Date().toISOString(),
                    maxParticipants: baseFormData.maxParticipants,
                    description: baseFormData.description,
                    teamsLink: meetingResponse.teamsLink,
                    id_meeting: meetingResponse.meetingId,
                    organizer_email: accounts[0].username,
                    organizer_name: accounts[0].name,
                    accessToken: accounts[0].idTokenClaims.oid,
                    refreshToken: tokenData.idToken,
                    tokenExpiry: new Date().getTime() + tokenData.expiresIn * 1000,
                    status: 'active'  // Add status field for soft delete functionality
                };
            }));

            createdMeetings.forEach(meeting => onMeetingCreated(meeting));
            
            // Reset form
            setBaseFormData({ titleBase: '', description: '', maxParticipants: '' });
            setMeetings([{ id: 1, date: '', startTime: '', endTime: '', selectedRoom: null }]);
        } catch (error) {
            setError(error.message || 'Failed to create meetings. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    

    return (
        <div className="p-8">
            <h1 className="text-4xl font-avenir font-900 text-center text-white mb-8">Create Meeting Series</h1>
            
            {error && (
                <div className="bg-red-900/20 border border-red-700 text-white p-4 rounded-lg mb-6">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl mx-auto">
                <div className="bg-vibrantRed backdrop-blur-sm rounded-lg p-6 space-y-4 border border-gray-700">
                    <div>
                        <label className="block text-white text-sm font-medium mb-2 font-avenir">Title</label>
                        <input
                            type="text"
                            value={baseFormData.titleBase}
                            onChange={(e) => setBaseFormData(prev => ({ ...prev, titleBase: e.target.value }))}
                            className="w-full bg-gray-700/50 text-white rounded-md p-2 border border-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                            required
                            placeholder="Enter meeting title"
                        />
                    </div>

                    <div>
                        <label className="block text-white text-sm font-medium mb-2 font-avenir">Description</label>
                        <textarea
                            value={baseFormData.description}
                            onChange={(e) => setBaseFormData(prev => ({ ...prev, description: e.target.value }))}
                            className="w-full bg-gray-700/50 text-white rounded-md p-2 border border-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                            rows="4"
                            required
                            placeholder="Enter meeting description"
                        />
                    </div>

                    <div>
                        <label className="block text-white font-avenir text-sm font-medium mb-2">Maximum Participants</label>
                        <input
                            type="number"
                            value={baseFormData.maxParticipants}
                            onChange={(e) => setBaseFormData(prev => ({ ...prev, maxParticipants: e.target.value }))}
                            className="w-full bg-gray-700 text-white rounded-md p-2 border border-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                            required
                            min="1"
                            placeholder="Enter maximum participants"
                        />
                    </div>
                </div>

                <div className="space-y-4">
                    {meetings.map((meeting) => (
                        <div key={meeting.id} className="bg-vibrantRed backdrop-blur-sm rounded-lg p-6 border border-gray-700 hover:border-gray-600 transition-colors">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-avenir font-semibold text-white">Session {meeting.id}</h3>
                                {meetings.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveMeeting(meeting.id)}
                                        className="text-red-800 hover:text-red-400"
                                    >
                                        <Minus size={20} />
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-white font-avenir text-sm font-medium mb-2">
                                        <Calendar className="inline mr-2" size={16} />
                                        Date
                                    </label>
                                    <input
                                        type="date"
                                        value={meeting.date}
                                        onChange={(e) => setMeetings(prev => prev.map(m => 
                                            m.id === meeting.id ? { ...m, date: e.target.value } : m
                                        ))}
                                        className="w-full bg-gray-700/50 text-white rounded-md p-2 border border-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                                        required
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-white font-avenir text-sm font-medium mb-2">
                                            <Clock className="inline mr-2" size={16} />
                                            Start Time
                                        </label>
                                        <div className="relative">
                                            <select
                                                type="time"
                                                value={meeting.startTime}
                                                onChange={(e) => setMeetings(prev => prev.map(m => 
                                                    m.id === meeting.id ? { ...m, startTime: e.target.value } : m
                                                ))}
                                                className="w-full bg-gray-700 text-white rounded-md p-2 border border-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                                                required
                                                
                                            >
                                                {Array.from({ length: 24 }, (_, hour) =>
                                                [0, 30].map((minute) => {
                                                    const formattedHour = hour.toString().padStart(2, "0");
                                                    const formattedMinute = minute.toString().padStart(2, "0");
                                                    return (
                                                        <option key={`${hour}:${minute}`} value={`${formattedHour}:${formattedMinute}`}>
                                                            {formattedHour}:{formattedMinute}
                                                        </option>
                                                    );
                                                })
                                            )}
                                        </select>
                                        </div>
                                    <style>
                                        {`
                                            select::-webkit-scrollbar {
                                                width: 8px;
                                            }

                                            select::-webkit-scrollbar-track {
                                                background: #444;  /* Darker background */
                                            }

                                            select::-webkit-scrollbar-thumb {
                                                background: #888;  /* Scrollbar color */
                                                border-radius: 4px;
                                            }

                                            select::-webkit-scrollbar-thumb:hover {
                                                background: #aaa;
                                            }
                                        `}
                                    </style>
                                    </div>

                                    <div>
                                        <label className="block text-white text-sm font-avenir font-medium mb-2">
                                            <Clock className="inline mr-2" size={16} />
                                            End Time
                                        </label>
                                        <select
                                            type="time"
                                            value={meeting.endTime}
                                            onChange={(e) => setMeetings(prev => prev.map(m => 
                                                m.id === meeting.id ? { ...m, endTime: e.target.value } : m
                                            ))}
                                            className="w-full bg-gray-700 text-white rounded-md p-2 border border-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                                            required
                                            >
                                            {Array.from({ length: 24 }, (_, hour) =>
                                            [0, 30].map((minute) => {
                                                const formattedHour = hour.toString().padStart(2, "0");
                                                const formattedMinute = minute.toString().padStart(2, "0");
                                                return (
                                                    <option key={`${hour}:${minute}`} value={`${formattedHour}:${formattedMinute}`}>
                                                        {formattedHour}:{formattedMinute}
                                                    </option>
                                                );
                                            })
                                        )}
                                    </select>
                                    </div>
                                </div>
                            </div>

                            {meeting.date && meeting.startTime && meeting.endTime && delegatedToken && (
                                <div className="mt-4">
                                    <RoomSelector
                                        accessToken={delegatedToken}
                                        startTime={`${meeting.date}T${meeting.startTime}`}
                                        endTime={`${meeting.date}T${meeting.endTime}`}
                                        onRoomSelect={(room) => setMeetings(prev => prev.map(m => 
                                            m.id === meeting.id ? { ...m, selectedRoom: room } : m
                                        ))}
                                    />
                                </div>
                            )}
                        </div>
                    ))}

                    <button
                        type="button"
                        onClick={handleAddMeeting}
                        className="w-full bg-vibrantRed hover:bg-red-800 font-avenir text-white p-4 rounded-lg transition flex items-center justify-center space-x-2"
                    >
                        <Plus size={20} />
                        <span>Add Another Session</span>
                    </button>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-black/80 hover:bg-red-800 font-avenir disabled:bg-gray-700/50 disabled:cursor-not-allowed text-white p-4 rounded-lg transition flex items-center justify-center space-x-2"
                >
                    <Save size={20} />
                    <span>{loading ? 'Creating Events...' : 'Create a Meeting'}</span>
                </button>
            </form>
        </div>
    );
};

export default CreateMeeting;